/* Hallmark · component-discipline · pdu-socket-matrix
 * Interactive PDU Outlet Matrix Popover.
 * Visual 24-outlet vertical strip (2x12) with C13/C19 differentiation,
 * connected consumer resolution (server + U-slot), and 1-click disconnect action.
 * Zero raw hex codes, zero emojis, theme-token styling.
 */
import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { api } from '../../utils/api';
import { useConfirmStore } from '../../store/confirmStore';
import {
  Zap,
  X,
  Unplug,
  Loader2,
  CheckCircle2,
} from 'lucide-react';
import type { RackEquipmentItem } from '../../hooks/useRackWorkspaceData';
import {
  buildPduOutletMatrix,
  type PduSocketItem,
} from '../../utils/pduOutletMatrix.logic';

export interface PduOutletMatrixPopoverProps {
  pdu: RackEquipmentItem;
  allEquipment: RackEquipmentItem[];
  datacenterCode?: string;
  rackId?: string | number;
  workspace?: string;
  onClose: () => void;
  onDisconnectSuccess?: () => void;
}

export function PduOutletMatrixPopover({
  pdu,
  allEquipment,
  datacenterCode: _dcCode,
  rackId: _rackId,
  workspace = 'master',
  onClose,
  onDisconnectSuccess,
}: PduOutletMatrixPopoverProps) {
  const queryClient = useQueryClient();
  const [selectedSocket, setSelectedSocket] = useState<PduSocketItem | null>(null);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  // Keyboard close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Fetch PDU ports
  const { data: pduPorts = [], isLoading: isPortsLoading } = useQuery<any[]>({
    queryKey: ['equipmentPorts', pdu.id, workspace],
    queryFn: () =>
      api.get(`/api/equipment/${pdu.id}/ports`, { params: { workspace } }).then(res => res.data),
    enabled: !!pdu.id,
  });

  // Fetch PDU cables
  const { data: pduCables = [], isLoading: isCablesLoading } = useQuery<any[]>({
    queryKey: ['equipmentCables', pdu.id, workspace],
    queryFn: () =>
      api.get(`/api/equipment/${pdu.id}/cables`, { params: { workspace } }).then(res => res.data),
    enabled: !!pdu.id,
  });

  const matrix = buildPduOutletMatrix({
    pdu,
    pduPorts,
    cables: pduCables,
    allEquipment,
  });

  const isLoading = isPortsLoading || isCablesLoading;

  // Disconnect active cable action with safety confirmation
  const handleDisconnect = async (socket: PduSocketItem) => {
    if (!socket.cableId) return;

    const confirmed = await useConfirmStore.getState().confirm({
      title: 'Отключение кабеля питания',
      message: `Отключить кабель питания от розетки ${socket.name}${
        socket.connectedDeviceName ? ` (подключен: ${socket.connectedDeviceName})` : ''
      }? Отключение питания в рабочей стойке может повлиять на доступность оборудования.`,
      confirmText: 'Отключить',
      cancelText: 'Отмена',
    });
    if (!confirmed) return;

    setIsDisconnecting(true);
    try {
      await api.delete(`/api/cables/${socket.cableId}`, {
        params: { workspace },
      });
      toast.success(`Розетка ${socket.name} отключена`);
      setSelectedSocket(null);
      queryClient.invalidateQueries({ queryKey: ['equipmentCables'] });
      queryClient.invalidateQueries({ queryKey: ['equipmentPorts'] });
      queryClient.invalidateQueries({ queryKey: ['powerSummary'] });
      queryClient.invalidateQueries({ queryKey: ['rackPower'] });
      queryClient.invalidateQueries({ queryKey: ['connectivity-graph'] });
      queryClient.invalidateQueries({ queryKey: ['rackEquipment'] });
      onDisconnectSuccess?.();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Ошибка отключения кабеля');
    } finally {
      setIsDisconnecting(false);
    }
  };

  const isFeedA = matrix.feedLetter === 'A';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`PDU Outlet Matrix: ${matrix.pduName}`}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-150"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-sm border p-4 flex flex-col gap-3 shadow-2xl transition-all"
        style={{
          background: 'var(--bg-panel)',
          borderColor: 'var(--border-color)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.55)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-2 border-b pb-3" style={{ borderColor: 'var(--border-color)' }}>
          <div className="flex items-center gap-2.5">
            <div
              className={`w-8 h-8 rounded-sm flex items-center justify-center shrink-0 border ${
                isFeedA
                  ? 'bg-cyan-950/40 border-cyan-500/40 text-cyan-400'
                  : 'bg-indigo-950/40 border-indigo-500/40 text-indigo-400'
              }`}
            >
              <Zap size={16} aria-hidden />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold font-mono tracking-wide" style={{ color: 'var(--text-primary)' }}>
                  {matrix.pduName}
                </h3>
                <span
                  className={`px-1.5 py-0.2 rounded-xs text-[9px] font-mono font-bold uppercase border ${
                    isFeedA
                      ? 'bg-cyan-950/40 text-cyan-300 border-cyan-700/50'
                      : 'bg-indigo-950/40 text-indigo-300 border-indigo-700/50'
                  }`}
                >
                  Feed {matrix.feedLetter}
                </span>
              </div>
              <p className="text-[10px] font-mono mt-0.5" style={{ color: 'var(--text-muted)' }}>
                Фаза {matrix.phase} · {matrix.ratedCurrentA}A · {matrix.totalOutlets} розеток
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span
              className={`px-2 py-0.5 rounded-xs text-[10px] font-mono font-bold border tabular-nums ${
                matrix.freeOutlets === 0
                  ? 'border-red-800/40 bg-red-950/40 text-red-300'
                  : matrix.freeOutlets <= 2
                  ? 'border-amber-800/40 bg-amber-950/40 text-amber-300'
                  : 'border-emerald-800/40 bg-emerald-950/40 text-emerald-300'
              }`}
            >
              {matrix.freeOutlets} / {matrix.totalOutlets} свободно
            </span>
            <button
              type="button"
              onClick={onClose}
              aria-label="Закрыть матрицу розеток"
              className="p-1 rounded-xs border border-transparent hover:border-[var(--border-color)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors cursor-pointer"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="py-12 flex flex-col items-center justify-center gap-2 text-text-muted">
            <Loader2 size={20} className="animate-spin text-accent" />
            <span className="text-xs font-mono">Опрос портов PDU...</span>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && matrix.totalOutlets === 0 && (
          <div className="py-8 text-center text-text-muted text-xs font-mono">
            На данном блоке PDU не обнаружено розеток питания.
          </div>
        )}

        {/* 2-Column Vertical Outlet Strip (2x12 Layout) */}
        {!isLoading && matrix.totalOutlets > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-[9px] font-mono text-text-muted px-1">
              <span className="uppercase tracking-wider">Матрица розеток PDU (Вид панели)</span>
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-xs border border-emerald-600 bg-emerald-950/40 inline-block" />
                  Свободна
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-xs border border-cyan-600 bg-cyan-950/60 inline-block" />
                  Подключена
                </span>
              </div>
            </div>

            <div
              className="p-2 rounded-xs border grid grid-cols-2 gap-1.5 max-h-72 overflow-y-auto"
              style={{
                background: 'var(--bg-app)',
                borderColor: 'var(--border-color)',
              }}
            >
              {matrix.sockets.map(sock => {
                const isSelected = selectedSocket?.portId === sock.portId;
                const isOccupied = sock.status === 'occupied';
                const isC19 = sock.connectorType === 'C19';

                return (
                  <button
                    key={sock.portId}
                    type="button"
                    onClick={() => setSelectedSocket(prev => (prev?.portId === sock.portId ? null : sock))}
                    aria-label={`Розетка #${sock.outletIndex}: ${sock.name}, ${sock.connectorType}, ${
                      isOccupied ? `Подключено: ${sock.connectedDeviceName || 'прибор'}` : 'Свободно'
                    }`}
                    className={`px-2 py-1.5 rounded-xs border text-left flex items-center justify-between gap-1.5 transition-all cursor-pointer select-none font-mono ${
                      isSelected
                        ? 'ring-2 ring-[var(--accent)] border-[var(--accent)] bg-[var(--bg-active)]'
                        : isOccupied
                        ? 'border-cyan-800/50 bg-cyan-950/20 hover:bg-cyan-950/40 text-cyan-200'
                        : 'border-emerald-800/30 bg-emerald-950/10 hover:bg-emerald-950/30 text-emerald-300'
                    }`}
                  >
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-[10px] font-bold shrink-0 opacity-75">
                        #{sock.outletIndex}
                      </span>
                      <span
                        className={`text-[8px] font-bold px-1 rounded-2xs border shrink-0 ${
                          isC19
                            ? 'bg-amber-500/15 border-amber-500/40 text-amber-600 dark:text-amber-300'
                            : 'bg-bg-input border-border text-text-secondary'
                        }`}
                      >
                        {sock.connectorType}
                      </span>
                      <span className="text-[10px] truncate" title={sock.connectedDeviceName || sock.name}>
                        {sock.connectedDeviceName ? sock.connectedDeviceName : 'Свободно'}
                      </span>
                    </div>

                    <div className="shrink-0 flex items-center">
                      {isOccupied ? (
                        <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" title="Подключено" />
                      ) : (
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 opacity-60" title="Свободно" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Selected Socket Detail & Quick-Action Card */}
            {selectedSocket ? (
              <div
                className="p-3 rounded-xs border flex flex-col gap-2 font-mono text-xs animate-in fade-in"
                style={{
                  background: 'var(--bg-app)',
                  borderColor: 'var(--border-color)',
                }}
              >
                <div className="flex items-center justify-between border-b pb-2" style={{ borderColor: 'var(--border-color)' }}>
                  <div className="flex items-center gap-1.5">
                    <span className="font-bold text-text-primary">
                      {selectedSocket.name}
                    </span>
                    <span
                      className={`text-[9px] px-1.5 py-0.5 rounded-2xs font-bold border ${
                        selectedSocket.connectorType === 'C19'
                          ? 'border-amber-500/40 bg-amber-500/15 text-amber-600 dark:text-amber-300'
                          : 'border-border bg-bg-panel text-text-secondary'
                      }`}
                    >
                      {selectedSocket.connectorType} (
                      {selectedSocket.connectorType === 'C19' ? '16A High-Draw' : '10A Standard'})
                    </span>
                  </div>

                  <span
                    className={`text-[10px] font-bold px-1.5 py-0.5 rounded-2xs border ${
                      selectedSocket.status === 'occupied'
                        ? 'border-cyan-500/40 bg-cyan-500/15 text-cyan-700 dark:text-cyan-300'
                        : 'border-emerald-500/40 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                    }`}
                  >
                    {selectedSocket.status === 'occupied' ? 'Подключено' : 'Свободна'}
                  </span>
                </div>

                {selectedSocket.status === 'occupied' ? (
                  <div className="flex flex-col gap-2 pt-1">
                    <div className="grid grid-cols-2 gap-2 text-[10px]">
                      <div>
                        <span className="text-text-muted block">Подключенный прибор:</span>
                        <span className="font-bold text-text-primary truncate block" title={selectedSocket.connectedDeviceName}>
                          {selectedSocket.connectedDeviceName}
                        </span>
                      </div>
                      <div>
                        <span className="text-text-muted block">Позиция в стойке:</span>
                        <span className="font-bold text-cyan-400 block">
                          {selectedSocket.connectedDeviceU ? `U${selectedSocket.connectedDeviceU}` : '0U'}
                        </span>
                      </div>
                      <div>
                        <span className="text-text-muted block">Ввод питания сервера:</span>
                        <span className="font-bold text-text-primary block">
                          {selectedSocket.connectedPortName || 'PSU'}
                        </span>
                      </div>
                      {selectedSocket.powerDrawW !== undefined && (
                        <div>
                          <span className="text-text-muted block">Мощность оборудования:</span>
                          <span className="font-bold text-amber-400 block">
                            {selectedSocket.powerDrawW} Вт
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end pt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
                      <button
                        type="button"
                        disabled={isDisconnecting}
                        onClick={() => handleDisconnect(selectedSocket)}
                        className="px-3 py-1.5 rounded-xs border border-red-800/50 bg-red-950/40 hover:bg-red-900/60 text-red-200 text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer disabled:opacity-50"
                      >
                        {isDisconnecting ? (
                          <Loader2 size={13} className="animate-spin" />
                        ) : (
                          <Unplug size={13} />
                        )}
                        <span>Отключить кабель</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-[10px] text-text-muted py-1 flex items-center gap-1.5">
                    <CheckCircle2 size={13} className="text-emerald-400" />
                    <span>Розетка свободна для подключения оборудования к вводу {matrix.feedLetter}.</span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-[10px] font-mono text-center text-text-muted py-1">
                Нажмите на любую розетку для просмотра подключенного сервера или отключения кабеля.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
